import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/shared/lib/cn';
import { fmtARS, fmtARSCompact, fmtInt, fmtVar, varSign } from './dashFormat';
import type { VentasSeriePunto } from './ventasApi';

/**
 * Charts del segmento Ventas. Paleta categórica validada con el skill
 * `/dataviz` (validate_palette.js — modo light). Marcas finas, extremos
 * redondeados, grilla recesiva, labels directos y hover por defecto.
 */

// Paleta categórica (slots en orden fijo — la identidad manda, no el rango).
export const CAT_PALETTE = [
  '#2a78d6', // 1 azul
  '#008300', // 2 verde
  '#e87ba4', // 3 magenta
  '#eda100', // 4 amarillo
  '#1baf7a', // 5 aqua
  '#eb6834', // 6 naranja
  '#4a3aa7', // 7 violeta
  '#e34948', // 8 rojo
] as const;
const CAT_OTRAS = '#898781'; // gris muted para categorías más allá del slot 8
export const SERIE_AZUL = '#2a78d6';

// Chip de los data labels del line chart: fondo azul suave + tinta azul.
const LABEL_BG = '#e8f1fc';
const LABEL_BORDER = '#c9def7';
const LABEL_INK = '#1c5cab';

/** Mapa categoría → color estable, según orden provisto (por total desc). */
export function buildCatColor(categorias: string[]): (cat: string) => string {
  const map = new Map<string, string>();
  categorias.forEach((c, i) => {
    map.set(c, i < CAT_PALETTE.length ? CAT_PALETTE[i] : CAT_OTRAS);
  });
  return (cat: string) => map.get(cat) ?? CAT_OTRAS;
}

/** Ancho del contenedor vía ResizeObserver (para SVG responsivo con hover exacto). */
export function useMeasuredWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

// ---------------------------------------------------------------------------
// Evolución de ventas — line chart
// ---------------------------------------------------------------------------

type Medida = 'monto' | 'unidades';

export function EvolucionVentasChart({
  series,
  medida,
}: {
  series: VentasSeriePunto[];
  medida: Medida;
}) {
  const { ref, width } = useMeasuredWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const H = 260;
  const PAD = { top: 16, right: 16, bottom: 28, left: 56 };
  const W = Math.max(width, 320);
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const val = (p: VentasSeriePunto) => (medida === 'monto' ? p.total : p.unidades);
  const fmtVal = (n: number) => (medida === 'monto' ? fmtARSCompact(n) : fmtInt(n));

  const maxV = Math.max(1, ...series.map(val));
  // Redondeo "lindo" del tope para la grilla.
  const niceMax = niceCeil(maxV);
  const n = series.length;

  const x = (i: number) =>
    PAD.left + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const y = (v: number) => PAD.top + innerH - (v / niceMax) * innerH;

  const linePath = series
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(val(p)).toFixed(1)}`)
    .join(' ');
  const areaPath =
    n > 0
      ? `${linePath} L ${x(n - 1).toFixed(1)} ${(PAD.top + innerH).toFixed(1)} ` +
        `L ${x(0).toFixed(1)} ${(PAD.top + innerH).toFixed(1)} Z`
      : '';

  const ticks = 4;
  const gridVals = Array.from({ length: ticks + 1 }, (_, i) => (niceMax / ticks) * i);
  // Etiquetas de mes: si hay muchas, mostrá 1 de cada k para no encimar.
  const step = Math.ceil(n / Math.max(1, Math.floor(innerW / 64)));

  function onMove(e: React.MouseEvent<SVGRectElement>) {
    if (n === 0) return;
    // El overlay arranca en SVG x=PAD.left, así que `px` ya es la distancia
    // desde ese borde (el SVG rinde 1:1 con px de pantalla).
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const i = Math.round((px / innerW) * (n - 1));
    setHover(Math.min(n - 1, Math.max(0, i)));
  }

  const hv = hover != null ? series[hover] : null;

  return (
    <div ref={ref} className="relative w-full">
      <svg width={W} height={H} role="img" aria-label="Evolución de ventas mensual">
        {/* grilla + labels Y */}
        {gridVals.map((gv, i) => (
          <g key={i}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(gv)}
              y2={y(gv)}
              className="text-border"
              stroke="currentColor"
              strokeWidth={1}
              opacity={i === 0 ? 0.9 : 0.5}
            />
            <text
              x={PAD.left - 8}
              y={y(gv)}
              dy="0.32em"
              textAnchor="end"
              className="fill-muted-foreground"
              style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}
            >
              {fmtVal(gv)}
            </text>
          </g>
        ))}

        {/* labels X */}
        {series.map((p, i) =>
          i % step === 0 || i === n - 1 ? (
            <text
              key={p.mes}
              x={x(i)}
              y={H - 8}
              textAnchor="middle"
              className="fill-muted-foreground"
              style={{ fontSize: 11 }}
            >
              {p.label}
            </text>
          ) : null,
        )}

        {/* área + línea */}
        {areaPath && <path d={areaPath} fill={SERIE_AZUL} opacity={0.08} />}
        <path
          d={linePath}
          fill="none"
          stroke={SERIE_AZUL}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* puntos */}
        {series.map((p, i) => (
          <circle
            key={p.mes}
            cx={x(i)}
            cy={y(val(p))}
            r={hover === i ? 5 : 3}
            fill={SERIE_AZUL}
            className="stroke-card"
            strokeWidth={2}
          />
        ))}

        {/* labels de datos: chip redondeado con fondo, anti-encimado y
            clampeado a los bordes para que no se corten */}
        {series.map((p, i) => {
          const v = val(p);
          if (v <= 0) return null;
          const txt = fmtVal(v);
          const py = y(v);
          const arriba = py - PAD.top > 26; // ¿hay lugar arriba del punto?
          const cy = arriba ? py - 14 : py + 15;
          const w = txt.length * 6.1 + 12;
          const h = 17;
          const cx = Math.min(
            Math.max(x(i), PAD.left + w / 2),
            W - PAD.right - w / 2,
          );
          return (
            <g key={`lbl-${p.mes}`}>
              <rect
                x={cx - w / 2}
                y={cy - h / 2}
                width={w}
                height={h}
                rx={8.5}
                ry={8.5}
                fill={LABEL_BG}
                stroke={LABEL_BORDER}
                strokeWidth={1}
              />
              <text
                x={cx}
                y={cy}
                textAnchor="middle"
                dominantBaseline="central"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  fill: LABEL_INK,
                }}
              >
                {txt}
              </text>
            </g>
          );
        })}

        {/* crosshair */}
        {hv && (
          <line
            x1={x(hover!)}
            x2={x(hover!)}
            y1={PAD.top}
            y2={PAD.top + innerH}
            className="text-muted-foreground"
            stroke="currentColor"
            strokeWidth={1}
            strokeDasharray="3 3"
            opacity={0.7}
          />
        )}

        {/* overlay para hover */}
        <rect
          x={PAD.left}
          y={PAD.top}
          width={innerW}
          height={innerH}
          fill="transparent"
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        />
      </svg>

      {hv && (
        <div
          className="pointer-events-none absolute z-10 min-w-[9.5rem] rounded-md border bg-popover px-3 py-2 text-xs shadow-md"
          style={{
            left: Math.min(Math.max(x(hover!) - 76, 0), W - 168),
            top: PAD.top,
          }}
        >
          <div className="mb-1 font-medium text-foreground">{hv.label}</div>
          <TipRow label="Monto" value={fmtARS(hv.total)} />
          <TipRow label="Unidades" value={`${fmtInt(hv.unidades)} u`} />
          {hv.operaciones != null && <TipRow label="Operaciones" value={fmtInt(hv.operaciones)} />}
          {hover! > 0 &&
            (() => {
              const prev = val(series[hover! - 1]);
              const s = varSign(val(hv), prev);
              const color = s > 0 ? '#0ca30c' : s < 0 ? '#d03b3b' : undefined;
              return (
                <div className="mt-1 border-t pt-1" style={color ? { color } : undefined}>
                  <span className={cn(s === 0 && 'text-muted-foreground')}>
                    {s > 0 ? '▲' : s < 0 ? '▼' : '■'} {fmtVar(val(hv), prev)} vs mes ant.
                  </span>
                </div>
              );
            })()}
        </div>
      )}
    </div>
  );
}

/** Fila label→valor para los tooltips de los charts. */
function TipRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums text-foreground">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Barras apiladas (ventas por mes, segmentos = artículos)
// ---------------------------------------------------------------------------

export type StackSeries = { key: string; color: string; values: number[] };

export function StackedBarsChart({
  labels,
  series,
  formatValue,
  formatCompact,
}: {
  labels: string[];
  series: StackSeries[];
  /** Formato completo (tooltip). */
  formatValue: (n: number) => string;
  /** Formato compacto (ejes / total sobre la barra). */
  formatCompact: (n: number) => string;
}) {
  const { ref, width } = useMeasuredWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const H = 300;
  const PAD = { top: 20, right: 16, bottom: 28, left: 60 };
  const W = Math.max(width, 320);
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const n = labels.length;

  const totals = labels.map((_, i) => series.reduce((s, ser) => s + (ser.values[i] || 0), 0));
  const niceMax = niceCeil(Math.max(1, ...totals));
  const slot = innerW / Math.max(1, n);
  const bw = Math.min(46, slot * 0.68);
  const y = (v: number) => PAD.top + innerH - (v / niceMax) * innerH;
  const ticks = 4;
  const gridVals = Array.from({ length: ticks + 1 }, (_, i) => (niceMax / ticks) * i);
  const step = Math.ceil(n / Math.max(1, Math.floor(innerW / 64)));

  const hv = hover != null ? hover : null;
  const breakdown =
    hv != null
      ? series
          .map((ser) => ({ key: ser.key, color: ser.color, value: ser.values[hv] || 0 }))
          .filter((s) => s.value > 0)
          .sort((a, b) => b.value - a.value)
      : [];

  return (
    <div ref={ref} className="relative w-full">
      <svg width={W} height={H} role="img" aria-label="Ventas por mes (apiladas por artículo)">
        {gridVals.map((gv, i) => (
          <g key={i}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(gv)}
              y2={y(gv)}
              className="text-border"
              stroke="currentColor"
              strokeWidth={1}
              opacity={i === 0 ? 0.9 : 0.5}
            />
            <text
              x={PAD.left - 8}
              y={y(gv)}
              dy="0.32em"
              textAnchor="end"
              className="fill-muted-foreground"
              style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}
            >
              {formatCompact(gv)}
            </text>
          </g>
        ))}

        {labels.map((label, i) => {
          const x0 = PAD.left + slot * i + (slot - bw) / 2;
          let acc = 0;
          const dim = hv != null && hv !== i;
          return (
            <g key={label}>
              {series.map((ser) => {
                const v = ser.values[i] || 0;
                if (v <= 0) return null;
                const yTop = y(acc + v);
                const h = Math.max(0, y(acc) - yTop);
                acc += v;
                return (
                  <rect
                    key={ser.key}
                    x={x0}
                    y={yTop}
                    width={bw}
                    height={h}
                    rx={2}
                    fill={ser.color}
                    className="stroke-card"
                    strokeWidth={1.5}
                    opacity={dim ? 0.4 : 1}
                  />
                );
              })}
              {totals[i] > 0 && (
                <text
                  x={x0 + bw / 2}
                  y={y(totals[i]) - 6}
                  textAnchor="middle"
                  className="fill-foreground"
                  style={{ fontSize: 10, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatCompact(totals[i])}
                </text>
              )}
              {(i % step === 0 || i === n - 1) && (
                <text
                  x={x0 + bw / 2}
                  y={H - 8}
                  textAnchor="middle"
                  className="fill-muted-foreground"
                  style={{ fontSize: 11 }}
                >
                  {label}
                </text>
              )}
              <rect
                x={PAD.left + slot * i}
                y={PAD.top}
                width={slot}
                height={innerH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover((h) => (h === i ? null : h))}
              />
            </g>
          );
        })}
      </svg>

      {hv != null && breakdown.length > 0 && (
        <div
          className="pointer-events-none absolute z-10 min-w-[11rem] max-w-[16rem] rounded-md border bg-popover px-3 py-2 text-xs shadow-md"
          style={{
            left: Math.min(PAD.left + slot * hv + slot, W - 200),
            top: PAD.top,
          }}
        >
          <div className="mb-1 flex items-baseline justify-between gap-3 font-medium text-foreground">
            <span>{labels[hv]}</span>
            <span className="tabular-nums">{formatValue(totals[hv])}</span>
          </div>
          <ul className="flex flex-col gap-0.5">
            {breakdown.slice(0, 8).map((b) => (
              <li key={b.key} className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: b.color }} />
                <span className="min-w-0 flex-1 truncate text-muted-foreground">{b.key}</span>
                <span className="shrink-0 tabular-nums text-foreground">{formatValue(b.value)}</span>
              </li>
            ))}
            {breakdown.length > 8 && (
              <li className="pl-3.5 text-muted-foreground">+{breakdown.length - 8} más…</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function niceCeil(v: number): number {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / mag;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * mag;
}

// ---------------------------------------------------------------------------
// Ranking horizontal (clientes / artículos) — barras HTML
// ---------------------------------------------------------------------------

export type RankRow = {
  label: string;
  value: number;
  color: string;
  /** Texto del valor a la derecha (ya formateado). */
  valueText: string;
  /** Línea secundaria opcional en el tooltip / bajo el label. */
  sub?: string;
};

export function RankBars({
  rows,
  emptyText,
  total,
}: {
  rows: RankRow[];
  emptyText: string;
  /** Total para el % del tooltip. Default: suma de las filas mostradas. */
  total?: number;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<{ i: number; x: number; y: number } | null>(null);

  const max = Math.max(1, ...rows.map((r) => r.value));
  const sumTotal = total ?? rows.reduce((s, r) => s + r.value, 0);

  if (rows.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
        {emptyText}
      </div>
    );
  }

  const onMove = (i: number) => (e: React.MouseEvent<HTMLLIElement>) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTip({ i, x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const hovered = tip ? rows[tip.i] : null;

  return (
    <div ref={wrapRef} className="relative">
      <ol className="flex flex-col gap-2.5">
        {rows.map((r, i) => (
          <li
            key={r.label + i}
            onMouseMove={onMove(i)}
            onMouseLeave={() => setTip((t) => (t?.i === i ? null : t))}
            className={cn(
              'grid grid-cols-[1.25rem_1fr] items-center gap-2 rounded-md px-1 py-0.5 transition-colors',
              tip?.i === i && 'bg-muted/60',
            )}
          >
            <span className="text-right text-xs tabular-nums text-muted-foreground">{i + 1}</span>
            <div className="min-w-0">
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <span className="truncate text-sm text-foreground">{r.label}</span>
                <span className="shrink-0 text-sm font-medium tabular-nums text-foreground">
                  {r.valueText}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full transition-[width] duration-500"
                  style={{ width: `${(r.value / max) * 100}%`, backgroundColor: r.color }}
                />
              </div>
            </div>
          </li>
        ))}
      </ol>

      {hovered && tip && (
        <div
          className="pointer-events-none absolute z-20 min-w-[10rem] max-w-[16rem] rounded-md border bg-popover px-3 py-2 text-xs shadow-md"
          style={{
            left: Math.min(tip.x + 12, (wrapRef.current?.clientWidth ?? 0) - 180),
            top: tip.y + 12,
          }}
        >
          <div className="mb-1 flex items-center gap-1.5 font-medium text-foreground">
            <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: hovered.color }} />
            <span className="break-words">{hovered.label}</span>
          </div>
          <TipRow label="Valor" value={hovered.valueText} />
          {hovered.sub && <TipRow label="Detalle" value={hovered.sub} />}
          <TipRow label="% del total" value={pct(hovered.value, sumTotal)} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Donut (composición parte-de-un-todo)
// ---------------------------------------------------------------------------

export type DonutSlice = {
  label: string;
  value: number;
  color: string;
  /** Info extra que aparece en el centro al hacer hover (p. ej. unidades). */
  sub?: string;
};

function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const a = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

function arcPath(cx: number, cy: number, R: number, r: number, a0: number, a1: number): string {
  const [x1, y1] = polar(cx, cy, R, a0);
  const [x2, y2] = polar(cx, cy, R, a1);
  const [x3, y3] = polar(cx, cy, r, a1);
  const [x4, y4] = polar(cx, cy, r, a0);
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${R} ${R} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} L ${x3.toFixed(2)} ${y3.toFixed(2)} A ${r} ${r} 0 ${large} 0 ${x4.toFixed(2)} ${y4.toFixed(2)} Z`;
}

/**
 * Donut con leyenda. El centro muestra el total; al pasar el mouse por un
 * gajo (o su fila en la leyenda) muestra ese gajo (label + valor + %).
 * Gap de 2px entre gajos vía stroke del color de la superficie (card).
 */
export function DonutChart({
  slices,
  formatValue,
  centerLabel = 'Total',
  emptyText,
}: {
  slices: DonutSlice[];
  formatValue: (n: number) => string;
  centerLabel?: string;
  emptyText: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const total = slices.reduce((s, x) => s + x.value, 0);
  const visibles = slices.filter((s) => s.value > 0);

  if (total <= 0 || visibles.length === 0) {
    return (
      <div className="flex h-44 items-center justify-center text-sm text-muted-foreground">
        {emptyText}
      </div>
    );
  }

  const SIZE = 180;
  const cx = SIZE / 2;
  const cy = SIZE / 2;
  const R = 84;
  const r = 52;
  const GAP = 1.6; // grados de separación entre gajos

  // Ángulos por slice (sobre el array original para mapear hover por índice).
  let acc = 0;
  const segs = slices.map((s) => {
    const frac = s.value / total;
    const a0 = acc * 360;
    acc += frac;
    const a1 = acc * 360;
    return { a0, a1, frac };
  });

  const soloUno = visibles.length === 1;
  const center = hover != null && slices[hover] ? slices[hover] : null;

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="h-44 w-44 shrink-0"
        role="img"
        aria-label={centerLabel}
      >
        {soloUno ? (
          <>
            <circle cx={cx} cy={cy} r={(R + r) / 2} fill="none" stroke={visibles[0].color} strokeWidth={R - r} />
          </>
        ) : (
          slices.map((s, i) => {
            if (s.value <= 0) return null;
            const seg = segs[i];
            const a0 = seg.a0 + GAP / 2;
            const a1 = seg.a1 - GAP / 2;
            if (a1 <= a0) return null;
            const dim = hover != null && hover !== i;
            return (
              <path
                key={s.label + i}
                d={arcPath(cx, cy, R, r, a0, a1)}
                fill={s.color}
                className="stroke-card"
                strokeWidth={2}
                opacity={dim ? 0.4 : 1}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                style={{ transition: 'opacity 150ms' }}
              />
            );
          })
        )}
        {/* Centro: total o gajo en hover */}
        <text
          x={cx}
          y={center ? cy - 8 : cy - 4}
          textAnchor="middle"
          className="fill-muted-foreground"
          style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}
        >
          {center ? truncar(center.label, 16) : centerLabel}
        </text>
        <text
          x={cx}
          y={center ? cy + 8 : cy + 12}
          textAnchor="middle"
          className="fill-foreground"
          style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
        >
          {formatValue(center ? center.value : total)}
        </text>
        {center && (
          <text
            x={cx}
            y={cy + 24}
            textAnchor="middle"
            className="fill-muted-foreground"
            style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}
          >
            {pct(center.value, total)}
            {center.sub ? ` · ${center.sub}` : ''}
          </text>
        )}
      </svg>

      {/* Leyenda */}
      <ol className="flex min-w-0 flex-1 flex-col gap-1.5 self-stretch">
        {slices.map((s, i) => (
          <li
            key={s.label + i}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            className={cn(
              'flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors',
              hover === i && 'bg-muted',
            )}
          >
            <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: s.color }} />
            <span className="min-w-0 flex-1 truncate text-sm text-foreground">{s.label}</span>
            <span className="shrink-0 text-sm font-medium tabular-nums text-foreground">
              {formatValue(s.value)}
            </span>
            <span className="w-12 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
              {pct(s.value, total)}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function pct(v: number, total: number): string {
  if (total <= 0) return '0%';
  return `${((v / total) * 100).toLocaleString('es-AR', { maximumFractionDigits: 1 })}%`;
}

function truncar(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

/** Leyenda de categorías (swatch + nombre). */
export function CategoriaLegend({
  categorias,
  color,
}: {
  categorias: string[];
  color: (c: string) => string;
}) {
  const shown = useMemo(() => categorias.slice(0, CAT_PALETTE.length), [categorias]);
  const overflow = categorias.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {shown.map((c) => (
        <span key={c} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
            style={{ backgroundColor: color(c) }}
          />
          {c}
        </span>
      ))}
      {overflow > 0 && (
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: CAT_OTRAS }} />
          Otras ({overflow})
        </span>
      )}
    </div>
  );
}
